import { NavLink, Outlet, useNavigate, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ShoppingCart,
  Package,
  ClipboardList,
  Users,
  LogOut,
  Wrench,
  Target,
  Trophy,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { getTheme } from "../lib/unitTheme";
import { api } from "../lib/api";
import MetaPopup from "../components/MetaPopup";
import AssistantWidget from "../components/AssistantWidget";

const NAV = [
  { to: "", label: "Nova Venda", icon: ShoppingCart, end: true, testid: "nav-nova-venda" },
  { to: "estoque", label: "Estoque", icon: Package, testid: "nav-estoque" },
  { to: "historico", label: "Histórico", icon: ClipboardList, testid: "nav-historico" },
  { to: "clientes", label: "Clientes", icon: Users, testid: "nav-clientes" },
  { to: "metas", label: "Metas", icon: Target, testid: "nav-metas" },
  { to: "ranking", label: "Ranking", icon: Trophy, testid: "nav-ranking" },
];

export default function UnidadeLayout() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { session, logout } = useAuth();
  const theme = getTheme(slug);
  const [metaAtiva, setMetaAtiva] = useState(null);
  const [metaPopupOpen, setMetaPopupOpen] = useState(false);

  useEffect(() => {
    if (!session || session.tipo !== "unidade" || session.unidade.slug !== slug) {
      navigate("/");
    }
  }, [session, slug, navigate]);

  // Carrega meta ativa e mostra popup automaticamente uma vez por sessão
  useEffect(() => {
    if (!session || session.tipo !== "unidade") return;
    api
      .get("/metas/ativa", { params: { unidade_id: session.unidade.id } })
      .then((r) => {
        setMetaAtiva(r.data);
        const key = `meta_popup_shown_${session.unidade.id}_${r.data.id}`;
        if (!sessionStorage.getItem(key)) {
          setMetaPopupOpen(true);
          sessionStorage.setItem(key, "1");
        }
      })
      .catch(() => setMetaAtiva(null));
  }, [session]);

  if (!session || session.tipo !== "unidade") return null;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-neutral-50 flex flex-col">
      <header className={`${theme.header} px-4 sm:px-6 h-16 flex items-center justify-between`}>
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-white/20 flex items-center justify-center border border-white/30">
            <Wrench className="w-4 h-4" strokeWidth={2.5} />
          </div>
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] opacity-80">
              Terminal Unidade
            </p>
            <h1 className="font-display font-black text-lg tracking-tight">
              {session.unidade.nome}
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {metaAtiva && (
            <button
              data-testid="meta-popup-open-btn"
              onClick={() => setMetaPopupOpen(true)}
              className={`flex items-center gap-2 px-3 h-10 font-mono text-xs uppercase tracking-widest transition-colors border ${
                metaAtiva.bate_meta_tendencia
                  ? "bg-green-600 border-green-700 text-white hover:bg-green-700"
                  : "bg-red-600 border-red-700 text-white hover:bg-red-700 animate-pulse"
              }`}
              title="Ver meta"
            >
              <Target className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">
                {metaAtiva.percentual_faturamento}% · {metaAtiva.bate_meta_tendencia ? "No ritmo" : "Abaixo"}
              </span>
            </button>
          )}
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 h-10 font-mono text-xs uppercase tracking-widest transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col md:flex-row">
        <aside className="w-full md:w-56 md:border-r-2 border-neutral-900 bg-white">
          <nav className="flex md:flex-col overflow-x-auto md:overflow-visible">
            {NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  end={item.end}
                  to={item.to}
                  data-testid={item.testid}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 md:py-4 font-display font-bold text-sm uppercase tracking-wide transition-colors border-b md:border-b-2 md:border-l-4 border-neutral-200 md:border-l-transparent whitespace-nowrap ${
                      isActive ? theme.activeNav : `text-neutral-700 ${theme.hoverNav}`
                    }`
                  }
                >
                  <Icon className="w-4 h-4" strokeWidth={2.5} />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">
          <Outlet context={{ unidade: session.unidade, theme }} />
        </main>
      </div>

      <MetaPopup
        open={metaPopupOpen && !!metaAtiva}
        onClose={() => setMetaPopupOpen(false)}
        metas={metaAtiva ? [{ ...metaAtiva, unidade_nome: session.unidade.nome }] : []}
        title={`${session.unidade.nome} · Meta ativa`}
      />

      <AssistantWidget
        escopo="unidade"
        unidadeId={session.unidade.id}
        unidadeNome={session.unidade.nome}
      />
    </div>
  );
}
